/**
 * The texts that bring them back, one by one, as the engine would send
 * them. Reminders only: nothing here asks for a reply (questions go to the
 * webchat). Each carries the one link that matters and says what is waiting
 * behind it, so a text read on the way to work is enough to decide on.
 *
 * The ladder after a sealed case: the morning it opens; a nudge the next
 * day; a word at three days with no lecture; one last text at seven, then
 * quiet until they come back on their own. A fast doer (two filed in a day)
 * is sent to bed instead, their case sealed either way.
 */
import type { Door } from "@/features/funnel/types";

const DAY_MS = 86_400_000;

export interface ComebackText {
  id: "unseal" | "nudge" | "lapse" | "final" | "double" | "tomorrow";
  /** when it goes, ms epoch */
  at: number;
  body: string;
  /** the step its link opens (`?go=`), when it has one */
  go?: string;
}

export interface ComebackState {
  name: string;
  handler: string | null;
  sealed: { door: Door; label: string; at: number } | null;
  /** missions filed, and when the last one was */
  filedCount: number;
  lastFiledAt: number | null;
  /** filed today, for the fast doer */
  filedToday: number;
  now: number;
}

const first = (name: string) => name.trim().split(/\s+/)[0] || "operative";

export const unsealText = (name: string, label: string) =>
  `${first(name)}, your case is ready. The door: ${label.toUpperCase()}. It's on your desk until midnight. One step is enough to start.`;

export function textsFor(s: ComebackState): ComebackText[] {
  const who = first(s.name);
  const out: ComebackText[] = [];
  if (s.filedToday >= 2) {
    out.push({
      id: "double",
      at: s.now,
      body: `Two in one day, ${who}. The Enemy noticed. Tomorrow's case is created either way: take tonight off.`,
    });
  }
  if (s.sealed) {
    const t0 = s.sealed.at;
    out.push({
      id: "unseal",
      at: t0,
      body: unsealText(s.name, s.sealed.label),
      go: "Doors",
    });
    out.push({
      id: "nudge",
      at: t0 + DAY_MS,
      body: `Your door's still here, ${who}. Nothing's been lost. The one-minute version counts.`,
      go: "Doors",
    });
    out.push({
      id: "lapse",
      at: t0 + 3 * DAY_MS,
      body: `Missed a few days. The Enemy was counting on it. One door, one small step, and your file picks up where it was.`,
      go: "Doors",
    });
    out.push({
      id: "final",
      at: t0 + 7 * DAY_MS,
      body: `Last text from the agency for a while, ${who}. Your file stays open and your locks stay open. Your door is here when you are.`,
      go: "Sign off",
    });
  } else if (s.filedCount > 0 && s.lastFiledAt) {
    out.push({
      id: "tomorrow",
      at: s.lastFiledAt + DAY_MS,
      body: `Morning, ${who}. Two new doors are up. Pick one before the day fills up.`,
      go: "Doors",
    });
  }
  return out.sort((a, b) => a.at - b.at);
}

/** How a text's time reads next to it: today, tomorrow, or the day. */
export const whenText = (at: number, now: number) => {
  const days = Math.round((startOfDay(at) - startOfDay(now)) / DAY_MS);
  const time = new Date(at).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (days <= 0) return `Today ${time}`;
  if (days === 1) return `Tomorrow ${time}`;
  return `In ${days} days · ${time}`;
};

const startOfDay = (t: number) => {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
